import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/common/Toast';

export function useWallet() {
  const [wallet, setWallet]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const fetch = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/me/wallet');
      setWallet(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load your wallet.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { wallet, loading, error, refetch: fetch };
}

export function useCashbackClaims() {
  const [claims, setClaims]   = useState([]);
  const [meta, setMeta]       = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/me/cashback-claims');
        setClaims(data.data || []);
        setMeta(data.meta ?? data);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  return { claims, meta, loading };
}

export function useRedemptions() {
  const [redemptions, setRedemptions] = useState([]);
  const [meta, setMeta]               = useState({});
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/me/redemptions');
        setRedemptions(data.data || []);
        setMeta(data.meta ?? data);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  return { redemptions, meta, loading };
}

export function usePayouts() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/me/payouts');
      setPayouts(data.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const requestPayout = async (amount) => {
    const { data } = await api.post('/me/payouts', { amount });
    await fetch();
    return data;
  };

  return { payouts, loading, refetch: fetch, requestPayout };
}

export function useStripeConnect() {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const { toast }             = useToast();

  const startOnboarding = async () => {
    setLoading(true); setError('');
    try {
      const origin = window.location.origin;
      const { data } = await api.post('/me/stripe-connect/onboard', {
        return_url:  `${origin}/profile/wallet?stripe=connected`,
        refresh_url: `${origin}/profile/wallet?stripe=refresh`,
        country:     'US',
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      // Backend returns 503 with {error, detail} when Stripe credentials
      // are unset on this environment. Surface as a toast instead of a
      // bare 500 — the user can see exactly what's missing and admins
      // know to add Stripe creds in Payment Settings.
      const title  = err.response?.data?.error  || 'Could not start onboarding.';
      const detail = err.response?.data?.detail || null;
      setError(title);
      toast.error(title, detail);
      setLoading(false);
    }
  };

  const refresh = async () => {
    try { const { data } = await api.get('/me/stripe-connect/refresh'); return data; }
    catch (err) { setError(err.response?.data?.error || 'Refresh failed.'); return null; }
  };

  return { startOnboarding, refresh, loading, error };
}
